import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft } from 'lucide-react';

const AccountBalance = () => {
  const navigate = useNavigate();
  const [account, setAccount] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [transactionLoading, setTransactionLoading] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [relatedData, setRelatedData] = useState(null);

  const pageSize = 10;

  useEffect(() => {
    fetchAccountInfo();
  }, []);

  useEffect(() => {
    fetchTransactions();
  }, [currentPage, searchTerm]);

  const fetchAccountInfo = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.get('/api/jifen/account', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAccount(response.data);
    } catch (err) {
      setError('获取账户信息失败');
      console.error('获取账户信息失败:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchTransactions = async () => {
    setTransactionLoading(true);
    try {
      const token = localStorage.getItem('access_token');
      const params = {
        page: currentPage,
        per_page: pageSize
      };
      const response = await axios.get('/api/jifen/integral/history', {
        headers: { Authorization: `Bearer ${token}` },
        params
      });
      setTransactions(response.data.data || []);
      setTotalPages(Math.ceil((response.data.total || 0) / pageSize));
    } catch (err) {
      console.error('获取交易记录失败:', err);
      // 如果接口不存在，暂时显示空数据
      setTransactions([]);
      setTotalPages(1);
    } finally {
      setTransactionLoading(false);
    }
  };

  const getStatusText = (status) => {
    const statusMap = {
      1: '正常',
      2: '冻结(违规)',
      3: '注销',
      4: '锁定(安全)'
    };
    return statusMap[status] || '未知';
  };

  const getStatusColor = (status) => {
    const colorMap = {
      1: 'text-green-600 bg-green-100',
      2: 'text-red-600 bg-red-100',
      3: 'text-gray-600 bg-gray-100',
      4: 'text-yellow-600 bg-yellow-100'
    };
    return colorMap[status] || 'text-gray-600 bg-gray-100';
  };

  const getTransactionTypeText = (type) => {
    const typeMap = {
      1: '充值',
      2: '收益',
      3: '消费',
      4: '退款',
      5: '扣减',
      6: '兑换',
      7: '赠送',
      8: '调整'
    };
    return typeMap[type] || '其他';
  };

  const formatDateTime = (value) => {
    if (!value) return '-';
    return new Intl.DateTimeFormat('zh-CN', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false
    }).format(new Date(value));
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setCurrentPage(1);
    fetchTransactions();
  };

  const handleViewDetail = async (transaction) => {
    setSelectedTransaction(transaction);
    setShowDetailModal(true);
    setRelatedData(null);
    
    // 如果有相关ID且不是系统模块，尝试获取相关详情
    if (transaction.related_id && transaction.business_module && transaction.business_module !== 'system') {
      await fetchRelatedDetail(transaction);
    }
  };

  const fetchRelatedDetail = async (transaction) => {
    setDetailLoading(true);
    try {
      const token = localStorage.getItem('access_token');
      let apiUrl = '';
      
      // 根据业务模块确定API路径
      switch (transaction.business_module) {
        case 'bazi':
          apiUrl = `/api/bazi/${transaction.related_id}`;
          break;
        case 'liuyao':
          apiUrl = `/api/liuyao/${transaction.related_id}`;
          break;
        case 'qimen':
          apiUrl = `/api/qimen/${transaction.related_id}`;
          break;
        default:
          setDetailLoading(false);
          return;
      }
      
      const response = await axios.get(apiUrl, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        setRelatedData(response.data.data);
      }
    } catch (error) {
      console.error('获取相关详情失败:', error);
      // 不显示错误，只是不显示相关数据
    } finally {
      setDetailLoading(false);
    }
  };

  const closeDetailModal = () => {
    setShowDetailModal(false);
    setSelectedTransaction(null);
    setRelatedData(null);
    setDetailLoading(false);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-md p-4">
        <p className="text-red-600">{error}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* 账户信息头部 */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between gap-3">
          {/* 账户标题、状态和统计信息 */}
          <div className="flex items-center gap-4 min-w-0 flex-1">
            <div className="flex items-center gap-2 shrink-0 -ml-2">
              <button
                onClick={() => navigate(-1)}
                className="p-1 hover:bg-gray-100 rounded-lg transition-colors"
                aria-label="返回上一页"
              >
                <ArrowLeft className="w-5 h-5 text-gray-600" />
              </button>
               <span className={`inline-flex items-center h-10 px-3 rounded-full text-xs font-medium ${getStatusColor(account?.status)}`}>
                {getStatusText(account?.status)}
              </span>
            </div>

            {/* 账户统计信息 */}
            <div className="grid grid-cols-2 grid-rows-2 gap-x-4 gap-y-0 h-10 text-xs text-gray-600 items-center min-w-0">
              <div className="flex items-center gap-1 whitespace-nowrap">
                <span>当前余额:</span>
                <span className="font-semibold text-blue-600 leading-none">{account?.balance || 0}</span>
              </div>
              <div className="flex items-center gap-1 whitespace-nowrap">
                <span>累计充值:</span>
                <span className="font-semibold text-green-600 leading-none">{account?.total_recharge || 0}</span>
              </div>
              <div className="flex items-center gap-1 whitespace-nowrap">
                <span>赏金收益:</span>
                <span className="font-semibold text-purple-600 leading-none">{account?.total_income || 0}</span>
              </div>
              <div className="flex items-center gap-1 whitespace-nowrap">
                <span>累计消费:</span>
                <span className="font-semibold text-red-600 leading-none">{account?.total_expense || 0}</span>
              </div>
            </div>
          </div>

          {/* 操作按钮 */}
          <div className="flex space-x-3">
            <button
              onClick={() => navigate('/jifen/recharge')}
              className="bg-blue-500 hover:bg-blue-600 text-white h-10 px-4 rounded-lg text-sm font-medium transition duration-200"
            >
              充值
            </button>
          </div>
        </div>
      </div>

      {/* 积分流水 */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">积分流水</h3>
          <form onSubmit={handleSearch} className="flex space-x-2">
            <input
              type="text"
              placeholder="搜索交易..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <button
              type="submit"
              className="bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded-lg text-sm transition duration-200"
            >
              搜索
            </button>
          </form>
        </div>

        {transactionLoading ? (
          <div className="flex justify-center items-center h-32">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
          </div>
        ) : (
          <>
            <div className="overflow-hidden">
              <table className="w-full table-fixed divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="w-1/3 px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      时间
                    </th>
                    <th className="w-1/6 px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      类型
                    </th>
                    <th className="w-1/6 px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      变动
                    </th>
                    <th className="w-1/6 px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      余额
                    </th>
                    <th className="w-1/6 px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      操作
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {transactions.length === 0 ? (
                    <tr>
                      <td colSpan="5" className="px-3 py-4 text-center text-gray-500">
                        暂无交易记录
                      </td>
                    </tr>
                  ) : (
                    transactions.map((transaction, index) => (
                      <tr key={transaction.record_id || index} className="hover:bg-gray-50">
                        <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-900 truncate">
                          {formatDateTime(transaction.created_time)}
                        </td>
                        <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-900">
                          {getTransactionTypeText(transaction.integral_type)}
                        </td>
                        <td className="px-3 py-4 whitespace-nowrap text-sm font-medium">
                          <span className={transaction.change_amount > 0 ? 'text-green-600' : 'text-red-600'}>
                            {transaction.change_amount > 0 ? '+' : ''}{transaction.change_amount}
                          </span>
                        </td>
                        <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-900">
                          {transaction.balance}
                        </td>
                        <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-900">
                          <button
                            onClick={() => handleViewDetail(transaction)}
                            className="text-blue-600 hover:text-blue-900 hover:underline"
                          >
                            详情
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* 分页 */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between mt-4">
                <div className="text-sm text-gray-700">
                  第 {currentPage} 页，共 {totalPages} 页
                </div>
                <div className="flex space-x-2">
                  <button
                    onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                    disabled={currentPage === 1}
                    className="px-3 py-1 border border-gray-300 rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                  >
                    上一页
                  </button>
                  <button
                    onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                    disabled={currentPage === totalPages}
                    className="px-3 py-1 border border-gray-300 rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                  >
                    下一页
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* 详情弹窗 */}
      {showDetailModal && selectedTransaction && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[80vh] overflow-y-auto">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">交易详情</h3>
            </div>
            <div className="px-6 py-4 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">交易时间</label>
                  <p className="mt-1 text-sm text-gray-900">
                    {new Date(selectedTransaction.created_time).toLocaleString()}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">交易类型</label>
                  <p className="mt-1 text-sm text-gray-900">
                    {getTransactionTypeText(selectedTransaction.integral_type)}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">变动金额</label>
                  <p className={`mt-1 text-sm font-medium ${selectedTransaction.change_amount > 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {selectedTransaction.change_amount > 0 ? '+' : ''}{selectedTransaction.change_amount}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">账户余额</label>
                  <p className="mt-1 text-sm text-gray-900">{selectedTransaction.balance}</p>
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700">交易描述</label>
                <p className="mt-1 text-sm text-gray-900">
                  {selectedTransaction.remark || selectedTransaction.business_type || '-'}
                </p>
              </div>
              
              {selectedTransaction.record_id && (
                <div>
                  <label className="block text-sm font-medium text-gray-700">交易ID</label>
                  <p className="mt-1 text-sm text-gray-900">{selectedTransaction.record_id}</p>
                </div>
              )}

              {/* 相关卦例信息 */}
              {selectedTransaction.business_module && selectedTransaction.business_module !== 'system' && (
                <div className="border-t pt-4">
                  <h4 className="text-md font-medium text-gray-900 mb-3">相关信息</h4>
                  {detailLoading ? (
                    <div className="flex justify-center py-4">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500"></div>
                    </div>
                  ) : relatedData ? (
                    <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                      <div className="flex justify-between items-center">
                        <span className="text-sm font-medium text-gray-700">业务模块:</span>
                        <span className="text-sm text-gray-900 capitalize">{selectedTransaction.business_module}</span>
                      </div>
                      {relatedData.title && (
                        <div className="flex justify-between items-center">
                          <span className="text-sm font-medium text-gray-700">标题:</span>
                          <span className="text-sm text-gray-900">{relatedData.title}</span>
                        </div>
                      )}
                      {relatedData.created_time && (
                        <div className="flex justify-between items-center">
                          <span className="text-sm font-medium text-gray-700">创建时间:</span>
                          <span className="text-sm text-gray-900">
                            {new Date(relatedData.created_time).toLocaleString()}
                          </span>
                        </div>
                      )}
                      <div className="pt-2">
                        <button
                          onClick={() => {
                            // 根据模块类型跳转到对应的详情页面
                            const basePath = selectedTransaction.business_module === 'bazi' ? '/bazi' :
                                           selectedTransaction.business_module === 'liuyao' ? '/liuyao' : '/qimen';
                            navigate(`${basePath}/chart/${selectedTransaction.related_id}`);
                            closeDetailModal();
                          }}
                          className="text-blue-600 hover:text-blue-800 text-sm font-medium hover:underline"
                        >
                          查看完整卦例 →
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="text-sm text-gray-500 text-center py-4">
                      无法获取相关信息
                    </div>
                  )}
                </div>
              )}
            </div>
            <div className="px-6 py-4 border-t border-gray-200 flex justify-end space-x-3">
              <button
                onClick={closeDetailModal}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition duration-200"
              >
                关闭
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AccountBalance;